import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { queryKeys } from "@nasam/kernel";
import { useDebouncedValue } from "../../../shared/application/useDebouncedValue";
import type { AssetsListQuery, AssetsRepository } from "../domain";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface AssetOption {
  readonly value: string;
  readonly label: string;
}

export interface UseAssetOptionsResult {
  readonly options: readonly AssetOption[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
}

export interface UseAssetOptionsSettings {
  /** Leaves out the assets already in this branch (e.g. Asset Assignments'
   *  Assets picker). Filtered client-side -- `GET /assets` has no branch
   *  filter. */
  readonly excludeBranchId?: string | undefined;
  /** Offers only this branch's assets (e.g. Create Alert's "specific
   *  assets" picker, which filters by branch first). Client-side too. */
  readonly onlyBranchId?: string | undefined;
}

/**
 * Server-searched, paginated asset options for the `Combobox` async mode --
 * mirrors useBranchOptions/useCompanyOptions. Colocated with the repository
 * it reads (not in the feature that consumes it, e.g. devices' "Linked
 * Asset" picker) so any feature can reuse it without crossing a module
 * boundary the wrong way. `accountId` scopes the list for a staff caller
 * (a client caller is always limited to their own account by the backend
 * regardless, so it's meaningless -- and should stay omitted -- for them).
 * Only living, active assets are offered -- there's no use case yet for
 * linking a device to an inactive one.
 */
export function useAssetOptions(
  repository: AssetsRepository | undefined,
  accountId?: string,
  { excludeBranchId, onlyBranchId }: UseAssetOptionsSettings = {},
): UseAssetOptionsResult {
  const { i18n } = useTranslation();
  const isAr = i18n.language.startsWith("ar");
  const sortBy = isAr ? "nameAr" : "nameEn";

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const search =
    debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const isEnabled =
    repository !== undefined && typeof repository.list === "function";

  const listQuery = (page: number): AssetsListQuery => ({
    page,
    limit: PAGE_SIZE,
    sort: "asc",
    sortBy,
    status: "ACTIVE",
    ...(search === undefined ? {} : { search }),
    ...(accountId === undefined ? {} : { accountId }),
  });

  const query = useInfiniteQuery({
    queryKey: queryKeys.assets.list({
      limit: PAGE_SIZE,
      search,
      sort: "asc",
      sortBy,
      status: "ACTIVE",
      accountId,
    }),
    queryFn: ({ pageParam }) => repository!.list(listQuery(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.currentPage < lastPage.meta.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined,
    enabled: isEnabled,
  });

  const options = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data
          .filter(
            (asset) =>
              asset.branch.id !== excludeBranchId &&
              (onlyBranchId === undefined || asset.branch.id === onlyBranchId),
          )
          .map((asset) => ({
            value: asset.id,
            label: isAr
              ? asset.nameAr || asset.nameEn
              : asset.nameEn || asset.nameAr,
          })),
      ),
    [query.data, isAr, excludeBranchId, onlyBranchId],
  );

  // Filtering can leave the loaded pages short of a page's worth (or empty),
  // and the Combobox only asks for more when its end-of-list sentinel
  // scrolls back into view -- so top the list up here instead.
  const { hasNextPage, isFetching, fetchNextPage } = query;
  const isFiltered = excludeBranchId !== undefined || onlyBranchId !== undefined;
  useEffect(() => {
    if (!isFiltered) return;
    if (options.length >= PAGE_SIZE || !hasNextPage || isFetching) return;
    void fetchNextPage();
  }, [isFiltered, options.length, hasNextPage, isFetching, fetchNextPage]);

  return {
    options,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
  };
}
